import { useState } from "react";
import { Box, Popover, useTheme } from "@mui/material";
import { DotLegalButton, DotLegalHeader, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { SelectedTextResult } from "./textSelection.types";

const POPOVER_WIDTH = 440;
const CLAUSE_ROWS = 6;

export interface IClauseSelectionPopoverProps {
    /** Where the popover opens, snapshotted when the user clicked, so it cannot chase a moving selection. */
    position: { top: number; left: number };
    captured: SelectedTextResult;
    onConfirm: (result: SelectedTextResult) => void;
    onCancel: () => void;
}

export default function ClauseSelectionPopover(props: IClauseSelectionPopoverProps) {
    const theme = useTheme();
    const { translateString } = useTranslation();
    const [clause, setClause] = useState(props.captured.clause);
    const [section, setSection] = useState(props.captured.section ?? "");

    return (
        <Popover
            open
            anchorReference="anchorPosition"
            anchorPosition={props.position}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClose={props.onCancel}
        >
            <Box sx={{ width: POPOVER_WIDTH, maxWidth: "90vw", padding: theme.spacing(2) }}>
                <DotLegalHeader headerStyle="extraSmall" marginBottom={0.5}>
                    {translateString("clauseText")}
                </DotLegalHeader>
                <DotLegalTextField label="" value={clause} multiline rows={CLAUSE_ROWS} debounce={false} noMargin onChange={setClause} />

                <DotLegalHeader headerStyle="extraSmall" marginTop={2} marginBottom={0.5}>
                    {translateString("documentSection")}
                </DotLegalHeader>
                <DotLegalTextField label="" value={section} debounce={false} noMargin onChange={setSection} />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, marginTop: theme.spacing(2) }}>
                    <DotLegalButton buttonType="secondary" onClick={props.onCancel}>
                        {translateString("cancel")}
                    </DotLegalButton>
                    <DotLegalButton
                        buttonType="primary"
                        disabled={clause.trim().length === 0}
                        onClick={() =>
                            props.onConfirm({
                                clause: clause.trim(),
                                section: section.trim() || undefined,
                                pageNumber: props.captured.pageNumber,
                            })
                        }
                    >
                        {translateString("useAsClause")}
                    </DotLegalButton>
                </Box>
            </Box>
        </Popover>
    );
}
